import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { FiShoppingCart, FiClock, FiCheckCircle, FiBell, FiTrendingUp } from 'react-icons/fi';
import { ReportsChart } from '../../components/ReportsChart';

function StaffDashboard() {
  const { user } = useAuth();
  const { alert } = useModal();
  const [stats, setStats] = useState({
    todayOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalOrders: 0
  });
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchWaiterCalls();
    fetchRecentOrders();
    generateChartData();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchWaiterCalls();
    }, 5000);

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchWaiterCalls();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/pos/orders/active').catch(() => ({ data: { orders: [] } }));
      const orders = response.data?.orders || [];
      const today = new Date().toDateString();
      
      const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today).length;
      const pendingOrders = orders.filter(o => !['completed', 'cancelled'].includes(o.status)).length;
      const completedOrders = orders.filter(o => o.status === 'completed').length;

      setStats({
        todayOrders,
        pendingOrders,
        completedOrders,
        totalOrders: orders.length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const response = await api.get('/api/pos/orders/active').catch(() => ({ data: { orders: [] } }));
      const orders = response.data?.orders || [];
      const sortedOrders = orders
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecentOrders(sortedOrders);
    } catch (error) {
      console.error('Failed to fetch recent orders:', error);
    }
  };

  const fetchWaiterCalls = async () => {
    try {
      setLoadingCalls(true);
      const response = await api.get('/api/waiter-calls?status=pending');
      setWaiterCalls(response.data?.waiterCalls || []);
    } catch (error) {
      console.error('Failed to fetch waiter calls:', error);
    } finally {
      setLoadingCalls(false);
    }
  };

  const generateChartData = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const data = days.map((day) => ({
      name: day,
      value: Math.floor(Math.random() * 40) + 5,
    }));
    setChartData(data);
  };

  const markCallCompleted = async (callId) => {
    try {
      await api.patch(`/api/waiter-calls/${callId}/status`, { status: 'completed' });
      fetchWaiterCalls();
    } catch (error) {
      console.error('Failed to mark call as completed:', error);
      await alert('Failed to mark call as completed', 'Error', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  return (
    <Layout>
      <div className="h-full flex overflow-hidden">
        {/* Single purpose: Table Calls (Waiter Calls) */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 space-y-4 max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Table Calls</h2>
              <span className="text-xs text-gray-500">
                {waiterCalls.length > 0 ? `${waiterCalls.length} pending` : 'No active calls'}
              </span>
            </div>

            {waiterCalls.length > 0 ? (
              <div className="border-0 bg-white shadow-sm rounded-lg">
                <div className="p-4 border-0 pb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Pending Table Calls</h3>
                    <span className="text-xs text-gray-500">{waiterCalls.length} table(s)</span>
                  </div>
                </div>
                <div className="p-4 pt-0">
                  <div className="space-y-2">
                    {waiterCalls.map((call) => {
                      const getMinutesSinceCall = (timestamp) => {
                        if (!timestamp) return 0;
                        let requestTime = new Date(timestamp);
                        if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
                          requestTime = new Date(timestamp + 'Z');
                        }
                        const now = new Date();
                        const nowIST = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                        const requestIST = new Date(requestTime.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                        const diffMs = nowIST - requestIST;
                        return Math.max(0, Math.floor(diffMs / 60000));
                      };
                      
                      const minutesAgo = getMinutesSinceCall(call.requested_at);
                      
                      return (
                        <div
                          key={call.id}
                          className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-lg"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="bg-red-500 rounded-full p-1.5 animate-pulse">
                              <FiBell className="text-white text-sm" />
                            </div>
                            <div>
                              <p className="font-bold text-sm text-gray-900">
                                Table {call.tables?.table_number || call.table_number || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-600">
                                {minutesAgo === 0 ? 'just now' : `${minutesAgo}m ago`}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => markCallCompleted(call.id)}
                            className="bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 font-semibold text-xs transition-all"
                          >
                            Done
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-0 bg-white shadow-sm rounded-lg">
                <div className="p-6 text-center">
                  <FiBell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No active table calls right now.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    New requests from tables will appear here in real time.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default StaffDashboard;
